import { useState } from "react";
import { TextField } from "@mui/material";
import type { Money } from "../../api/types";
import { makeMoney } from "../../logic/money";

export type AmountActionFieldProps = {
  value: Money;
  onChange: (value: Money) => void;
};

/**
 * The amount an action sets.
 *
 * The box owns the text while it is being typed in. Wired straight to the
 * `Money` value it re-renders with the canonical, already-rounded string after
 * every keystroke, so a digit typed after the decimals makes `0.009`, rounds
 * back to `0.00`, and the keystroke looks like it never landed. The draft holds
 * exactly what was typed; the canonical form is written back on blur, so what
 * the box shows when you leave it is what the rule will save.
 *
 * Focus selects the contents. The field starts at `0.00` and a click puts the
 * caret where it landed — for a click on the right-hand side that is past the
 * decimals, the one spot where typing rounds away to nothing.
 */
export const AmountActionField = ({
  value,
  onChange,
}: AmountActionFieldProps) => {
  const [draft, setDraft] = useState(value.amount);

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      value={draft}
      // Committed per keystroke, not on blur: the surrounding form saves from
      // its own button, and a value typed but not blurred still has to land.
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(makeMoney(e.target.value, value.currency));
      }}
      onFocus={(e) => e.target.select()}
      onBlur={() => setDraft(makeMoney(draft, value.currency).amount)}
      slotProps={{ htmlInput: { step: "0.01", "aria-label": "Amount" } }}
    />
  );
};
